import React from "react";
import { Sector } from "recharts";
import styled from "styled-components";

import { RADIAN } from "@constants/radian";

import type { ActiveShape } from "recharts/types/util/types";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";

type ShapeProps<
  T extends PieSectorDataItem & { status: string; name: string },
> = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload: T;
  percent: number;
  value: string | number;
};

function ChartShape<
  T extends PieSectorDataItem & { status: string; name: string; value: number },
>({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
  percent,
}: ShapeProps<T>) {
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      <StyledTotalText x={cx} y={cy} dy={0} textAnchor="middle">
        TOTAL
      </StyledTotalText>

      <text x={cx} y={cy} dy={16} textAnchor="middle">
        {payload.name}
      </text>

      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />

      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle + 1}
        endAngle={endAngle - 1}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />

      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />

      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />

      <StyledText
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
      >
        {`${payload.status}`}
      </StyledText>

      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill="#999"
      >
        {`${payload.value} (${(percent * 100).toFixed(2)}%)`}
      </text>
    </g>
  );
}

const StyledText = styled.text`
  text-wrap: nowrap;
  white-space: nowrap;

  text-transform: uppercase;
`;

const StyledTotalText = styled.text`
  font-weight: 400;
`;

export default ChartShape as ActiveShape<PieSectorDataItem>;
